import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { Badge } from './ui/badge';
import { ProjectTagsDropdown } from './ProjectTagsDropdown';
import { isSystemTag } from '../utils/system-tags';
import { TAG_BADGE_STYLE } from '../utils/tag-badge-style';
import { truncateLabel } from '../utils/text';

const normalizeTagValue = (tag: string): string => tag.trim().toLowerCase();

interface StringTagsCellProps {
  tags: string[];
  availableTags: string[];
  onTagsChange: (tags: string[]) => Promise<void>;
  readOnly?: boolean;
  deferEditor?: boolean;
  emptyLabel?: React.ReactNode;
}

const TagBadges: React.FC<{
  tags: string[];
  showRemoveButtons?: boolean;
  onRemoveTag?: (tag: string) => void;
}> = ({ tags, showRemoveButtons = false, onRemoveTag }) => {
  const { t } = useLocale();

  return (
    <>
      {tags.map((tag) => (
        <Badge
          key={tag}
          variant="secondary"
          className="rounded-full px-2 py-0.5 text-xs flex items-center gap-1"
          style={TAG_BADGE_STYLE}
          title={tag}
        >
          <span>{truncateLabel(tag, 15)}</span>
          {showRemoveButtons && !isSystemTag(tag) && onRemoveTag && (
            <button
              type="button"
              aria-label={t('remove')}
              className="rounded-full hover:bg-muted p-0.5"
              onClick={(evt) => {
                evt.preventDefault();
                evt.stopPropagation();
                onRemoveTag(tag);
              }}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </Badge>
      ))}
    </>
  );
};

export const StringTagsCell: React.FC<StringTagsCellProps> = ({
  tags,
  availableTags,
  onTagsChange,
  readOnly = false,
  deferEditor = false,
  emptyLabel = '-',
}) => {
  const { t } = useLocale();
  const [isEditing, setIsEditing] = useState(false);
  const normalizedTags = tags.map(normalizeTagValue).filter(Boolean);
  const renderEmpty = (): React.ReactNode => {
    if (emptyLabel === null) {
      return null;
    }
    return <span className="text-muted-foreground text-sm">{emptyLabel}</span>;
  };

  if (readOnly) {
    if (normalizedTags.length === 0) {
      return renderEmpty();
    }
    return (
      <div className="flex flex-wrap items-center gap-2 w-full">
        <TagBadges tags={normalizedTags} />
      </div>
    );
  }

  if (deferEditor && !isEditing) {
    return (
      <button
        type="button"
        className="flex flex-wrap items-center gap-2 w-full cursor-pointer text-left"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
      >
        {normalizedTags.length === 0 ? renderEmpty() : <TagBadges tags={normalizedTags} />}
      </button>
    );
  }

  return (
    <ProjectTagsDropdown
      selectedTags={tags}
      availableTags={availableTags}
      onTagsChange={onTagsChange}
      initialOpen={deferEditor && isEditing}
      onOpenChange={(open) => {
        if (deferEditor && !open) {
          setIsEditing(false);
        }
      }}
      inputPlaceholder={t('Assign tags...')}
      contentClassName="w-[260px] p-2"
      createLabel={(tag) => `${t('Create tag')} ${tag}`}
      trigger={({ setIsOpen, selectedTags: normalizedSelectedTags, removeTag }) => (
        <button
          type="button"
          className="flex flex-wrap items-center gap-2 w-full cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
        >
          {normalizedSelectedTags.length === 0 ? (
            renderEmpty()
          ) : (
            <TagBadges tags={normalizedSelectedTags} showRemoveButtons onRemoveTag={removeTag} />
          )}
        </button>
      )}
    />
  );
};
